import { useEffect, useRef, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { trpc } from "@/trpc/client";
import { PageHeading } from "@/ui";
import {
  cloudflareDnsRedirectUri,
  consumeCloudflareDnsOAuthState,
} from "@/lib/cloudflare-oauth";
import { nextOnboardingStepAfterDomain } from "@/lib/onboardingFlow";
import { workspacesQueryOptions } from "@/lib/workspaces";

/**
 * Cloudflare's redirect back after DNS OAuth consent. The URL carries no
 * `workspaceId`: Cloudflare matches `redirect_uri` exactly against the app's
 * registered list, so there is one callback URL for every workspace and the
 * workspace/domain/flags are recovered from the OAuth `state` instead
 * (`lib/cloudflare-oauth.ts`).
 *
 * It still lives under `/dashboard`, so the authenticated-subtree guard in
 * `dashboard.tsx` applies; the `_` suffix keeps it out of any future
 * `/dashboard/domains` page's subtree while serving that URL prefix.
 *
 * This route is NOT under `/dashboard/w/$workspaceId`, so it has no AppShell.
 * `gs-app` is the same ground onboarding uses for that reason: without it the
 * page inherits the dark `styles.css` body while `--gs-*` tokens stay light.
 *
 * An onboarding-started flow (`fromOnboarding: true`) does not always return
 * to the domain screen: once both the DNS write and the claim succeed, it
 * advances straight to the next onboarding step via
 * `nextOnboardingStepAfterDomain` — the same "Updates, unless this is not
 * the registration workspace, then first-post" decision the domain screen's
 * own Continue/Skip/Verify already make — so onboarding is never held
 * waiting for propagation (doc-08 §2.2). Any failure exit (Cloudflare
 * `?error=`, a rejected write, a rejected claim) still returns to
 * `/onboarding/$workspaceId/domain` with today's error toast.
 *
 * Deliberately calls no `verifyDomain` (spec 989 FR-007): unlike the domain
 * screen's own manual-DNS Verify, which only advances once Laneful's
 * `verified` flag comes back `true`, this success exit fires as soon as the
 * DNS write and the claim both succeed — the claim already starts Laneful's
 * check server-side, and propagation finishes in the background rather than
 * blocking this screen on it.
 */
export const Route = createFileRoute("/dashboard/domains_/cloudflare-callback")({
  component: CloudflareDnsCallbackPage,
});

type CallbackOutcome = "connected" | "failed";

function goHome(
  navigate: ReturnType<typeof useNavigate>,
  pending: { workspaceId: string; fromOnboarding: boolean } | null,
  outcome: CallbackOutcome,
  workspaces: ReadonlyArray<{ workspaceId: string }> | undefined,
) {
  if (!pending) {
    void navigate({ to: "/dashboard" });
    return;
  }
  if (!pending.fromOnboarding) {
    void navigate({
      to: "/dashboard/w/$workspaceId/domains",
      params: { workspaceId: pending.workspaceId },
    });
    return;
  }
  if (outcome === "failed") {
    void navigate({
      to: "/onboarding/$workspaceId/domain",
      params: { workspaceId: pending.workspaceId },
    });
    return;
  }
  // Same decision `OnboardingDomain`'s own Continue/Skip/Verify make — a
  // non-registration workspace goes straight to `/first-post` rather than
  // bouncing off `/updates`'s own `guardUpdatesStep` redirect.
  void navigate(nextOnboardingStepAfterDomain(workspaces, pending.workspaceId));
}

function CloudflareDnsCallbackPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const processedRef = useRef(false);
  const [phase, setPhase] = useState<"dns" | "claim">("dns");

  const claimMutation = useMutation({
    ...trpc.authenticatedRouterFunction.setWorkspaceActiveDomain.mutationOptions(),
  });
  const configureMutation = useMutation({
    ...trpc.authenticatedRouterFunction.configureCloudflareDns.mutationOptions(),
  });

  const claiming = phase === "claim";

  useEffect(() => {
    if (processedRef.current) return;
    processedRef.current = true;

    const params = new URLSearchParams(window.location.search);
    const pending = consumeCloudflareDnsOAuthState(params.get("state"));
    window.history.replaceState({}, "", window.location.pathname);

    const finish = (
      outcome: CallbackOutcome,
      workspaces?: ReadonlyArray<{ workspaceId: string }>,
    ) => goHome(navigate, pending, outcome, workspaces);

    const error = params.get("error");
    if (error) {
      toast.error(
        "Cloudflare authorization did not complete. Please try again.",
      );
      finish("failed");
      return;
    }

    const code = params.get("code");
    if (!code || !pending) {
      toast.error("Invalid Cloudflare callback. Please try again.");
      finish("failed");
      return;
    }

    void (async () => {
      let outcome: CallbackOutcome = "failed";
      try {
        const result = await configureMutation.mutateAsync({
          workspaceId: pending.workspaceId,
          domain: pending.domain,
          code,
          redirectUri: cloudflareDnsRedirectUri(),
          includeBlog: pending.includeBlog,
        });
        setPhase("claim");
        try {
          await claimMutation.mutateAsync({
            workspaceId: pending.workspaceId,
            domain: pending.domain,
          });
          outcome = "connected";
          void queryClient.invalidateQueries({
            queryKey: trpc.authenticatedRouterFunction.getWorkspaceDomainLifecycle.queryOptions(
              { workspaceId: pending.workspaceId },
            ).queryKey,
          });
          void queryClient.invalidateQueries({
            queryKey: trpc.authenticatedRouterFunction.getBlog.queryOptions({
              workspaceId: pending.workspaceId,
            }).queryKey,
          });
          toast.success("Domain connected", {
            description: `${pending.domain} on ${result.zone}.`,
          });
        } catch (err) {
          toast.error("DNS is in place, but the domain isn't connected yet", {
            description:
              err instanceof Error
                ? err.message
                : "Connect it from the Domain page.",
          });
        }
      } catch (err) {
        toast.error("Couldn't update Cloudflare DNS", {
          description: err instanceof Error ? err.message : "Unknown error occurred.",
        });
      }
      // Only the connected+onboarding exit needs the workspaces list (to pick
      // Updates vs. first-post); every other exit skips the fetch entirely.
      // Awaited here rather than subscribed via `useQuery` — this route has
      // no ancestor `beforeLoad` prefetch and is reached by a cold external
      // redirect from Cloudflare, so a render-time subscription can still be
      // unresolved when this line runs. `fetchQuery` reads the same cache
      // `OnboardingDomain`'s `workspacesQuery` does, but resolves to the real
      // value (or `undefined` on failure) instead of racing it. On failure it
      // falls open the same way `nextOnboardingStepAfterDomain` already does
      // for an unresolved list (spec 1136 A3).
      const workspaces =
        outcome === "connected" && pending.fromOnboarding
          ? await queryClient.fetchQuery(workspacesQueryOptions).catch(() => undefined)
          : undefined;
      finish(outcome, workspaces);
    })();
    // One-shot on mount: consume deletes the single-use state, so a second run
    // could only ever fail validation. `processedRef` is the real guard.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <main className="gs-app flex justify-center px-6 py-16">
      <section className="flex w-full max-w-3xl flex-col gap-8">
        <PageHeading
          size="hero"
          eyebrow="Cloudflare"
          title="Connecting your domain"
          lede={
            claiming
              ? "DNS is in place. Connecting it to this workspace…"
              : "Adding the GoodSender records at Cloudflare…"
          }
        />
        <Loader2 className="size-6 animate-spin" aria-hidden />
      </section>
    </main>
  );
}
